/**
 * ASCII Art Generator - 特效处理器
 *
 * 管理多个特效的叠加顺序，按用户设置顺序应用特效
 * 重构：不再持有内部状态，从外部接收 EffectsState
 */

import type { EffectType, EffectsState, EffectConfig } from './types'
import { applyVignette } from './VignetteEffect'
import { applyNoise, NoiseEffect } from './NoiseEffect'
import { applyBeam, BeamEffect } from './BeamEffect'
import { applyIntervals, IntervalsEffect } from './IntervalsEffect'
import { applyWaves, WavesEffect } from './WavesEffect'
import { applyMatrix, MatrixEffect } from './MatrixEffect'
import { applyGlitch, GlitchEffect } from './GlitchEffect'
import { applyCRT, CRTEffect } from './CRTEffect'

// ============================================
// 特效应用映射
// ============================================

/** 特效应用函数类型 */
type EffectApplier = (canvas: HTMLCanvasElement, config: any) => void

/** 特效应用函数映射 */
const effectAppliers: Record<EffectType, EffectApplier> = {
  vignette: applyVignette,
  noise: applyNoise,
  beam: applyBeam,
  intervals: applyIntervals,
  waves: applyWaves,
  matrix: applyMatrix,
  glitch: applyGlitch,
  crt: applyCRT
}

// ============================================
// 特效处理器类
// ============================================

/**
 * 特效处理器
 * 无状态设计：从外部接收 EffectsState，不持有内部状态
 * 用于管理特效实例的时间更新和应用逻辑
 */
export class EffectsProcessor {
  private effects: Map<EffectType, any> = new Map()

  constructor() {
    // 初始化特效实例（用于需要时间更新的特效）
    this.initEffects()
  }

  /**
   * 初始化特效实例
   */
  private initEffects(): void {
    // 这些特效需要实例来管理时间更新
    this.effects.set('noise', new NoiseEffect({ strength: 0.5, scale: 2, density: 0.7, colored: false, time: 0 }))
    this.effects.set('beam', new BeamEffect({ strength: 0.7, width: 50, speed: 1.5, direction: 'right', color: '#ffffff', time: 0 }))
    this.effects.set('intervals', new IntervalsEffect({ spacing1: 15, width1: 4, spacing2: 25, width2: 3, direction: 'down', animated: false, time: 0 }))
    this.effects.set('waves', new WavesEffect({ strength: 0.6, frequency: 0.08, speed: 1.5, hueShift: 60, waveCount: 3, time: 0 }))
    this.effects.set('matrix', new MatrixEffect({ strength: 0.7, speed: 1.5, charSize: 16, trailLength: 20, density: 0.6, mode: 'overlay', time: 0 }))
    this.effects.set('glitch', new GlitchEffect({ rgbShift: 8, blockShift: 15, blockCount: 4, speed: 1.5, direction: 'right', time: 0 }))
    this.effects.set('crt', new CRTEffect({ scanlineStrength: 0.5, scanlineSpacing: 2, curvature: 0.25, edgeRgbShift: 4, crtVignette: 0.4, time: 0 }))
    // vignette 不需要类实例
  }

  /**
   * 更新所有特效实例的时间（用于动画）
   */
  update(deltaTime: number): void {
    // 更新需要动画的特效实例
    const beamEffect = this.effects.get('beam') as BeamEffect | undefined
    beamEffect?.update(deltaTime)

    const wavesEffect = this.effects.get('waves') as WavesEffect | undefined
    wavesEffect?.update(deltaTime)

    const matrixEffect = this.effects.get('matrix') as MatrixEffect | undefined
    matrixEffect?.update(deltaTime)

    const glitchEffect = this.effects.get('glitch') as GlitchEffect | undefined
    glitchEffect?.update(deltaTime)
  }

  /**
   * 同步特效实例配置（从外部状态）
   */
  syncEffectsFromState(state: EffectsState): void {
    for (const effectType of ['noise', 'beam', 'intervals', 'waves', 'matrix', 'glitch', 'crt'] as EffectType[]) {
      const effectState = state[effectType]
      if (effectState && 'config' in effectState) {
        const effectInstance = this.effects.get(effectType)
        if (effectInstance && typeof effectInstance.updateConfig === 'function') {
          effectInstance.updateConfig(effectState.config)
        }
      }
    }
  }

  /**
   * 应用所有启用的特效到画布
   * @param canvas 目标画布
   * @param state 外部特效状态（从 Zustand 获取）
   */
  apply(canvas: HTMLCanvasElement, state: EffectsState): void {
    // 按状态中的顺序应用特效
    for (const effectType of state.order) {
      const effectState = state[effectType]
      if (!effectState.enabled) continue

      // 直接使用应用函数和状态的 config
      const applier = effectAppliers[effectType]
      if (applier) {
        applier(canvas, effectState.config)
      }
    }
  }

  /**
   * 重置所有特效实例
   */
  reset(): void {
    for (const [_type, instance] of this.effects) {
      if (instance && typeof instance.reset === 'function') {
        instance.reset()
      }
    }
  }
}

// ============================================
// 便捷函数
// ============================================

/**
 * 创建默认特效处理器
 */
export function createEffectsProcessor(): EffectsProcessor {
  return new EffectsProcessor()
}

/**
 * 快速应用单个特效
 */
export function applySingleEffect(
  canvas: HTMLCanvasElement,
  effectType: EffectType,
  config: EffectConfig
): void {
  const applier = effectAppliers[effectType]
  if (applier) {
    applier(canvas, config)
  }
}